import { Component } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ModalController } from 'ionic-angular';

import { RegionService } from '../../services/region.service';

import { SessionService, RecommendationTracking } from '../../services/session.service';
import { Recommendation } from '../../types/session.type';
import { EmailCustomerPage } from '../email-customer/email-customer';
import { SMSCustomerPage } from '../sms-customer/sms-customer';
import { SendSuccessPage } from '../send-success/send-success';
import { ConfirmFinishPage } from '../confirm-finish/confirm-finish';
import { NetworkStatusService } from '../../services/network-status.service';
import { SharedConstants } from '../../services/shared/shared-constants';
import { TrackingService } from '../../services/tracking.service';
import { GtagService } from '../../services/gtag.service';
import { GA4Event } from '../../services/shared/models/gtag.model';

export interface GetFilePathForItemWithUniqueIdResponse {
  path: string;
}

@Component({
  selector: 'recommendation-page',
  templateUrl: 'recommendation.html',
})
export class RecommendationPage {
  preventShare: boolean;

  showContent: boolean;
  customizeActive: boolean;

  tabs: string[];
  currentPair: Recommendation;

  newSwatch: string;
  networkOnline: boolean;
  clearOldData = true;

  recommendationTrackingValues: RecommendationTracking[];

  constructor(
    private router: Router,
    public regionService: RegionService,
    public sanitizer: DomSanitizer,
    public sessionService: SessionService,
    public modalCtrl: ModalController,
    public networkService: NetworkStatusService,
    private gtagService: GtagService,
    private trackingService: TrackingService
  ) {
    this.tabs = ['Eyeglasses', 'Sunglasses'];
    this.sessionService.recommendations = {};

    this.sessionService.getRecommendations().subscribe((recommendationTrackingValues: RecommendationTracking[]) => {
      this.tabs = this.tabs.filter((tab: string) => this.sessionService.recommendations[tab]);

      this.currentPair = this.sessionService.recommendations.Eyeglasses;
      this.recommendationTrackingValues = recommendationTrackingValues;
      this.recommendationTrackingValues.forEach((recommendationTrackingValue) => {
        let analyticsEvent: GA4Event = {
          actionName: recommendationTrackingValue.name,
          params: {},
        };
        analyticsEvent.params['event_label'] = recommendationTrackingValue.label;
        analyticsEvent.params[recommendationTrackingValue.dimension] = recommendationTrackingValue.dimensionValue;
        this.gtagService.logEvent(analyticsEvent);
      });
      this.showContent = true;
    });

    this.networkService.online$.subscribe((res) => {
      this.networkOnline = res;
    });
  }

  private sendSuccess(showSuccessMessage): void {
    const sendSuccessModal = this.modalCtrl.create(
      SendSuccessPage,
      { showSuccessMessage: showSuccessMessage },
      { cssClass: 'confirm-modal' }
    );
    sendSuccessModal.present();
  }

  email(): void {
    let analyticsEvent: GA4Event = {
      actionName: "email-start",
      params: {},
    };
    let recommendation = this.recommendationTrackingValues.find((value) => value.name === 'recommendation');
    analyticsEvent.params['event_label'] = recommendation.label;
    analyticsEvent.params[recommendation.dimension] = recommendation.dimensionValue;
    this.gtagService.logEvent(analyticsEvent);
    if (this.networkOnline) {
      if (this.preventShare) return;
      this.preventShare = true;

      const emailCustomerModal = this.modalCtrl.create(
        EmailCustomerPage,
        { clearOldData: this.clearOldData },
        {
          cssClass: 'customer-modal',
          enableBackdropDismiss: false,
        }
      );
      emailCustomerModal.present();
      emailCustomerModal.onDidDismiss((res: boolean) => {
        this.preventShare = false;
        if (res) {
          this.sendSuccess(true);
          this.clearOldData = true;
          let analyticsEvent: GA4Event = {
            actionName: "email-success",
            params: {},
          };
          let recommendation = this.recommendationTrackingValues.find((value) => value.name === 'recommendation');
          analyticsEvent.params['event_label'] = recommendation.label;
          analyticsEvent.params[recommendation.dimension] = recommendation.dimensionValue;
          this.gtagService.logEvent(analyticsEvent);
        } else if (res === false) {
          this.clearOldData = false;
          this.sendSuccess(false);
        } else {
          this.clearOldData = false;
        }
      });
    } else {
      this.networkService.presentToast(SharedConstants.networkErrorMessage);
    }
  }

  sms(): void {
    if (this.networkOnline) {
      let analyticsEvent: GA4Event = {
        actionName: "sms-start",
        params: {},
      };
      let recommendation = this.recommendationTrackingValues.find((value) => value.name === 'recommendation');
      analyticsEvent.params['event_label'] = recommendation.label;
      analyticsEvent.params[recommendation.dimension] = recommendation.dimensionValue;
      this.gtagService.logEvent(analyticsEvent);
      if (this.preventShare) return;
      this.preventShare = true;

      const smsCustomerModal = this.modalCtrl.create(
        SMSCustomerPage,
        {},
        {
          cssClass: 'sms-modal',
          enableBackdropDismiss: false,
        }
      );
      smsCustomerModal.present();
      smsCustomerModal.onDidDismiss((res: boolean) => {
        this.preventShare = false;
        if (res) {
          console.log('sms success');
          let analyticsEvent: GA4Event = {
            actionName: "sms-success",
            params: {},
          };
          let recommendation = this.recommendationTrackingValues.find((value) => value.name === 'recommendation');
          analyticsEvent.params['event_label'] = recommendation.label;
          analyticsEvent.params[recommendation.dimension] = recommendation.dimensionValue;
          this.gtagService.logEvent(analyticsEvent);
          this.sendSuccess(true);
        } else if (res === false) {
          this.sendSuccess(false);
        }
      });
    } else {
      this.networkService.presentToast(SharedConstants.networkErrorMessage);
    }
  }

  navigateToVisionCenter() {
    let analyticsEvent: GA4Event = {
      actionName: "find-vision-center",
      params: {},
    };
    this.gtagService.logEvent(analyticsEvent);
    this.networkOnline
      ? window.open('https://www.zeiss.com/vision-care/us/search/find-an-eye-doctor-near-you.html', '_blank')
      : this.networkService.presentToast(SharedConstants.networkErrorMessage);
  }

  backgroundImage(imagePath: string): any {
    return this.sanitizer.bypassSecurityTrustStyle(imagePath ? `background-image: url(${imagePath})` : 'background-image: none');
  }

  prev(): void {
    this.networkOnline ? this.router.navigate(['visual-behaviour']) : this.networkService.presentToast(SharedConstants.networkErrorMessage);
    this.trackingService.resetTrackingValues();
  }

  confirmFinish(): void {
    if (this.networkOnline) {
      const confirmFinishModal = this.modalCtrl.create(ConfirmFinishPage, {}, { cssClass: 'confirm-modal' });
      confirmFinishModal.present();
      confirmFinishModal.onDidDismiss((res: boolean) => {
        if (res) {
          this.sessionService.init().subscribe(() => this.router.navigate(['home']));
        }
      });
    } else {
      this.networkService.presentToast(SharedConstants.networkErrorMessage);
    }
  }

  startCustomize(): void {
    this.networkOnline ? (this.customizeActive = true) : this.networkService.presentToast(SharedConstants.networkErrorMessage);
  }

  finishCustomize(clearSelection: boolean): void {
    if (clearSelection) {
      this.selectLens('Sunglasses');
      setTimeout(() => (this.customizeActive = false), 250);
    } else {
      this.customizeActive = false;
      this.sessionService.saveRecommendations();
    }
  }

  selectLens(swatch: string): void {
    if (!this.newSwatch) {
      this.newSwatch = swatch;

      setTimeout(() => {
        this.sessionService.customLens = swatch;
        this.trackingService.customLens = swatch;
        this.newSwatch = null;
      }, 250);
    }
  }

  selectPair(pair: string): void {
    this.networkOnline
      ? (this.currentPair = this.sessionService.recommendations[pair])
      : this.networkService.presentToast(SharedConstants.networkErrorMessage);
  }
}



// WEBPACK FOOTER //
// ./src/pages/recommendation/recommendation.ts